import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './VaadsMessages.module.css';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  MessageSquare,
  Calendar,
  Type,
  FileText,
  ArrowRight,
  Building,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit3,
  Trash2,
  MessageCircle,
  Share2,
  MoreVertical,
  ExternalLink,
  Mail,
  Send
} from 'lucide-react';
import {
  getVaadsMessages,
  createVaadsMessage,
  updateVaadsMessage,
  deleteVaadsMessage,
  addAnswerToMessage,
  markMessagesAsRead
} from '../../Fetch/fetchVaadsMessages';
import { getUserBuildings } from '../../Fetch/fetchUser';
import { useVaadsNotifications, NotificationDot } from '../../Pages/VaadsMessages/NotificationService';

// WhatsApp Style Answers Viewer Dialog Component with Integrated Answer Input
const AnswersViewerDialog = ({ message, isOpen, onClose, formatDate, currentUser, onSubmitAnswer, loading, onMarkAsRead }) => {
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const [answerText, setAnswerText] = useState('');
  const [answerError, setAnswerError] = useState('');
  const [localMessage, setLocalMessage] = useState(message);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Update local message when prop changes
  useEffect(() => {
    setLocalMessage(message);
  }, [message]);

  useEffect(() => {
    scrollToBottom();
  }, [localMessage?.answers]);

  // Mark message as read when dialog opens
  useEffect(() => {
    if (isOpen && localMessage?.id && onMarkAsRead) {
      onMarkAsRead(localMessage.id);
    }
  }, [isOpen, localMessage?.id, onMarkAsRead]);

  // Real-time message refreshing - ONLY when dialog is open
  useEffect(() => {
    if (!isOpen || !localMessage?.id) return;

    let pollInterval;

    const refreshMessage = async () => {
      try {
        // Get fresh message data
        const freshMessages = await getVaadsMessages();
        const freshMessage = freshMessages.find(m => m.id === localMessage.id);

        if (freshMessage && freshMessage.answers?.length !== localMessage.answers?.length) {
          setLocalMessage(freshMessage);

          // Dispatch event to parent to update main messages list
          window.dispatchEvent(new CustomEvent('messageAnswersUpdated', {
            detail: { messageId: freshMessage.id, message: freshMessage }
          }));
        }
      } catch (error) {
      }
    };

    // Poll every 3 seconds for new messages when dialog is open
    pollInterval = setInterval(refreshMessage, 3000);

    // Also listen for answer events from other users
    const handleNewAnswer = (event) => {
      if (event.detail.messageId === localMessage.id) {
        refreshMessage();
      }
    };

    window.addEventListener('vaadsMessageAnswered', handleNewAnswer);

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      window.removeEventListener('vaadsMessageAnswered', handleNewAnswer);
    };
  }, [isOpen, localMessage?.id, localMessage?.answers?.length]);

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();

    if (!answerText.trim()) {
      setAnswerError('תגובה לא יכולה להיות רקה');
      return;
    }

    if (answerText.length > 1000) {
      setAnswerError('תגובה לא יכולה להיות יותר מ-1000 תווים');
      return;
    }

    try {
      await onSubmitAnswer(answerText.trim());
      setAnswerText('');
      setAnswerError('');

      // IMPORTANT: Refocus the textarea to keep keyboard open on mobile
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100); // Small delay to ensure the state update is complete

      // Broadcast event for other users
      window.dispatchEvent(new CustomEvent('vaadsMessageAnswered', {
        detail: {
          messageId: localMessage.id,
          answerUserId: currentUser.userID,
          timestamp: new Date().toISOString()
        }
      }));

    } catch (error) {
      // Even on error, refocus to keep keyboard open
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSendButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent the button from taking focus away from textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    // Trigger the form submission
    handleAnswerSubmit(e);
  };

  const handleTextChange = (e) => {
    setAnswerText(e.target.value);
    setAnswerError('');
  };

  const handleClose = () => {
    setAnswerText('');
    setAnswerError('');
    onClose();
  };

  if (!isOpen || !localMessage) return null;

  const answers = localMessage.answers || [];

  return (
    <div className={styles.dialogOverlay}>
      <div className={styles.dialogContent} style={{ maxWidth: '700px', height: '80vh' }}>
        <div className={styles.dialogHeader}>
          <h3>💬 {localMessage?.headerMessage}</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className={styles.whatsappChat}>
          {/* Original Message */}
          <div className={styles.originalMessage}>
            <div className={styles.originalMessageHeader}>
              <div className={styles.originalMessageUser}>
                <User size={16} />
                <span>{localMessage.user?.fullName || 'לא ידוע'}</span>
              </div>
              <div className={styles.originalMessageTime}>
                {formatDate(localMessage.createDate)}
              </div>
            </div>
            <div className={styles.originalMessageContent}>
              <h4>{localMessage.headerMessage}</h4>
              <p>{localMessage.description}</p>
            </div>
          </div>

          {/* Chat Messages */}
          <div className={styles.chatContainer}>
            {answers.length === 0 ? (
              <div className={styles.noAnswers}>
                <MessageSquare size={48} className={styles.noAnswersIcon} />
                <h4>אין תגובות עדיין</h4>
                <p>היה הראשון להגיב להודעה זו</p>
              </div>
            ) : (
              <div className={styles.whatsappMessages}>
                {answers.map((answer, index) => {
                  const isCurrentUser = currentUser && answer.userID === currentUser.userID;

                  return (
                    <div
                      key={`answer-${localMessage.id}-${answer.id || answer.userID || 'unknown'}-${index}`}
                      className={`${styles.messageWrapper} ${isCurrentUser ? styles.myMessage : styles.otherMessage}`}
                    >
                      <div className={styles.messageBubble}>
                        <div className={styles.messageName}>
                          {answer.userName || 'משתמש לא ידוע'}
                        </div>
                        <div className={styles.messageText}>
                          {answer.text}
                        </div>
                        <div className={styles.messageTime}>
                          {formatDate(answer.createdAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Integrated Answer Input */}
          <div className={styles.chatInputContainer}>
            <form onSubmit={handleAnswerSubmit} className={styles.chatInputForm}>
              <div className={styles.chatInputWrapper}>
                <textarea
                  ref={textareaRef} // ADD THE REF HERE
                  value={answerText}
                  onChange={handleTextChange}
                  placeholder="כתוב תגובה..."
                  rows={1}
                  maxLength={1000}
                  className={`${styles.chatInput} ${answerError ? styles.error : ''}`}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAnswerSubmit(e);
                    }
                  }}
                  // ADD THESE MOBILE-SPECIFIC PROPS:
                  onBlur={(e) => {
                    // Prevent blur if the send button was clicked
                    const relatedTarget = e.relatedTarget;
                    if (relatedTarget && relatedTarget.classList.contains(styles.chatSendButton)) {
                      e.preventDefault();
                      setTimeout(() => {
                        if (textareaRef.current) {
                          textareaRef.current.focus();
                        }
                      }, 10);
                    }
                  }}
                />
                <button
                  type="button" // CHANGE FROM "submit" to "button"
                  className={styles.chatSendButton}
                  disabled={loading || !answerText.trim()}
                  onClick={handleSendButtonClick} // USE THE NEW HANDLER
                  onTouchStart={(e) => {
                    // Prevent focus loss on mobile touch
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    // Prevent focus loss
                    e.preventDefault();
                  }}
                >
                  {loading ? (
                    <div className={styles.sendingSpinner}></div>
                  ) : (
                    <Send size={20} />
                  )}
                </button>
              </div>
              {answerError && <div className={styles.chatInputError}>{answerError}</div>}
              <div className={styles.chatCharCount}>
                {answerText.length}/1000 תווים
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mobile Card Component
const VaadsMessageCard = ({
  message,
  onEdit,
  onDelete,
  onWhatsAppShare,
  onViewAnswers,
  formatDate,
  loading,
  getStatusIcon,
  getStatusColor,
  currentUser,
  newAnswerMessages,
  newMessages,
  getAnswerCount,
  isNewMessage
}) => (
  <div className={`${styles.messageCard} ${isNewMessage(message.id) ? styles.newMessageCard : ''}`}>
    <div className={styles.cardHeader}>
      <div className={styles.cardTitle}>
        <MessageSquare size={18} className={styles.cardIcon} />
        <span>{message.headerMessage}</span>
        {isNewMessage(message.id) && (
          <span className={styles.newBadge}>חדש!</span>
        )}
      </div>
      <div className={styles.cardActions}>
        <span className={`${styles.statusBadge} ${styles[getStatusColor(message.status)]}`}>
          {getStatusIcon(message.status)}
          {message.status || 'פעיל'}
        </span>
        <ActionMenu
          message={message}
          onEdit={onEdit}
          onDelete={onDelete}
          onWhatsAppShare={onWhatsAppShare}
          onViewAnswers={onViewAnswers}
          loading={loading}
          currentUser={currentUser}
        />
      </div>
    </div>

    <div className={styles.cardBody}>
      <div className={styles.cardRow}>
        <FileText size={16} />
        <span className={styles.messageDescription}>{message.description}</span>
      </div>

      <div className={styles.cardRow}>
        <Building size={16} />
        <span>בניין: {message.building?.address || 'לא צוין'}</span>
      </div>

      <div className={styles.cardRow}>
        <User size={16} />
        <span>נוצר על ידי: {message.user?.fullName || 'לא צוין'}</span>
      </div>

      <div className={styles.cardRow}>
        <Calendar size={16} />
        <span>נוצר: {formatDate(message.createDate)}</span>
      </div>

      {message.updatedDate && (
        <div className={styles.cardRow}>
          <Calendar size={16} />
          <span>עודכן: {formatDate(message.updatedDate)}</span>
        </div>
      )}

      <div className={styles.cardRow}>
        <MessageSquare size={16} />
        <div
          className={`${styles.answersCount} ${newAnswerMessages.has(message.id) ? styles.hasNewAnswers : ''
            } ${isNewMessage(message.id) ? styles.newMessage : ''
            }`}
          onClick={() => onViewAnswers(message)}
          title="לחץ לצפייה בתגובות"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 10px',
            borderRadius: '6px',
            border: (newAnswerMessages.has(message.id) || isNewMessage(message.id)) ? '2px solid #dc3545' : '2px solid #e9ecef',
            backgroundColor: (newAnswerMessages.has(message.id) || isNewMessage(message.id)) ? '#fff5f5' : '#f8f9fa',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            minWidth: '70px',
            justifyContent: 'center'
          }}
        >
          <MessageSquare size={14} color={(newAnswerMessages.has(message.id) || isNewMessage(message.id)) ? '#dc3545' : '#6c757d'} />
          <span
            className={styles.answerCountNumber}
            style={{
              fontWeight: (newAnswerMessages.has(message.id) || isNewMessage(message.id)) ? 'bold' : 'normal',
              color: (newAnswerMessages.has(message.id) || isNewMessage(message.id)) ? '#dc3545' : '#495057',
              fontSize: '13px'
            }}
          >
            {getAnswerCount(message.id)}
          </span>
          {(newAnswerMessages.has(message.id) || isNewMessage(message.id)) && (
            <NotificationDot
              show={true}
              size="small"
              className="answer-notification-mobile"
            />
          )}
        </div>
      </div>
    </div>
  </div>
);

// Form Component
const VaadsMessageForm = ({ message, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    headerMessage: message?.headerMessage || '',
    description: message?.description || '',
    status: message?.status || 'פעיל'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [currentBuilding, setCurrentBuilding] = useState(null);

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) {
        setCurrentUser({
          userID: userData.userId,
          fullName: userData.name,
          email: userData.email
        });
        setCurrentBuilding({
          buildingID: userData.buildingId,
          address: userData.selectedBuildingInfo?.address || 'לא צוין'
        });
      }
    } catch (error) {
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.headerMessage.trim()) {
      newErrors.headerMessage = 'כותרת היא שדה חובה';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'תיאור הוא שדה חובה';
    }

    if (!currentUser?.userID) {
      newErrors.user = 'משתמש לא זמין';
    }

    if (!currentBuilding?.buildingID) {
      newErrors.building = 'בניין לא זמין';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const messageData = {
        headerMessage: formData.headerMessage,
        description: formData.description,
        status: formData.status,
        userID: currentUser.userID,
        buildingID: currentBuilding.buildingID
      };

      let savedMessage;
      if (message) {
        savedMessage = await updateVaadsMessage(message.id, messageData);
      } else {
        savedMessage = await createVaadsMessage(messageData);
      }

      onSave(savedMessage);

    } catch (error) {
      alert('שגיאה בשמירת ההודעה: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formHeader}>
        <button
          className={styles.backButton}
          onClick={onCancel}
          type="button"
        >
          <ArrowRight size={20} />
        </button>
        <h2>{message ? 'עריכת הודעה משותפת' : 'הוספת הודעה משותפת חדשה'}</h2>
      </div>

      <form className={styles.messageForm} onSubmit={handleSubmit}>
        <div className={styles.formSection}>
          <h3>פרטי ההודעה</h3>

          <div className={styles.formGroup}>
            <label htmlFor="headerMessage">
              כותרת <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="headerMessage"
              name="headerMessage"
              value={formData.headerMessage}
              onChange={handleChange}
              className={errors.headerMessage ? styles.error : ''}
              placeholder="הכנס כותרת להודעה"
            />
            {errors.headerMessage && (
              <span className={styles.fieldError}>{errors.headerMessage}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">
              תיאור <span className={styles.required}>*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={errors.description ? styles.error : ''}
              placeholder="הכנס את תיאור ההודעה"
              rows={6}
              maxLength={1000}
            />
            {errors.description && (
              <span className={styles.fieldError}>{errors.description}</span>
            )}
            <div className={styles.charCount}>
              {formData.description.length}/1000 תווים
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="status">סטטוס</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={errors.status ? styles.error : ''}
            >
              <option value="פעיל">פעיל</option>
              <option value="טיפול">בטיפול</option>
              <option value="סגור">סגור</option>
              <option value="דחוי">דחוי</option>
            </select>
            {errors.status && (
              <span className={styles.fieldError}>{errors.status}</span>
            )}
          </div>

          <div className={styles.infoSection}>
            <div className={styles.infoItem}>
              <User size={16} />
              <span>משתמש: {currentUser?.fullName || 'טוען...'}</span>
            </div>
            <div className={styles.infoItem}>
              <Building size={16} />
              <span>בניין: {currentBuilding?.address || 'טוען...'}</span>
            </div>
          </div>

          {errors.user && <span className={styles.fieldError}>{errors.user}</span>}
          {errors.building && <span className={styles.fieldError}>{errors.building}</span>}
        </div>

        <div className={styles.formActions}>
           <button
            type="submit"
            className={styles.saveButton}
            disabled={loading || !currentUser?.userID || !currentBuilding?.buildingID}
          >
            {loading ? 'שומר...' : (message ? 'עדכן הודעה' : 'הוסף הודעה')}
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            ביטול
          </button>
         
        </div>
      </form>
    </div>
  );
};

// Action Menu Modal Component (replaces dropdown)
const ActionMenu = ({
  message,
  onEdit,
  onDelete,
  onWhatsAppShare,
  onViewAnswers,
  loading,
  currentUser
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const canEditDelete = currentUser && message.user?.userID === currentUser.userID;

  const actions = [
    {
      label: '💬 צפה והגב',
      onClick: () => onViewAnswers(message),
      disabled: loading
    },
    {
      label: '📱 שתף WhatsApp',
      onClick: () => onWhatsAppShare(message),
      disabled: loading
    }
  ];

  if (canEditDelete) {
    actions.push(
      {
        label: '✏️ ערוך',
        onClick: () => onEdit(message),
        disabled: loading
      },
      {
        label: '🗑️ מחק',
        onClick: () => onDelete(message.id),
        disabled: loading,
        isDanger: true
      }
    );
  }

  const handleActionClick = (action) => {
    if (!action.disabled) {
      action.onClick();
      setIsOpen(false);
    }
  };

  return (
    <>
      <button
        className={`${styles.actionMenuTrigger} ${loading ? styles.disabled : ''}`}
        onClick={() => setIsOpen(true)}
        disabled={loading}
        aria-label="פתח תפריט פעולות"
      >
        <span>⋯</span>
      </button>

      {isOpen && createPortal(
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.actionMenuModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.actionMenuHeader}>
              <h3>פעולות</h3>
              <button
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="סגור"
              >
                ✕
              </button>
            </div>
            <div className={styles.actionMenuContent}>
              {actions.map((action, index) => (
                <button
                  key={index}
                  className={`${styles.actionMenuItem} ${action.isDanger ? styles.dangerAction : ''} ${action.disabled ? styles.disabled : ''}`}
                  onClick={() => handleActionClick(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Main Component
const VaadsMessages = () => {
  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [messagesPerPage, setMessagesPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  // Answers viewer state
  const [showAnswersViewer, setShowAnswersViewer] = useState(false);
  const [viewingAnswersMessage, setViewingAnswersMessage] = useState(null);

  // Enhanced notification states using NotificationService
  const {
    hasUnread,
    newMessageCount,
    newAnswerCount,
    checkUnreadMessages,
    markAsRead
  } = useVaadsNotifications();

  const [newAnswerMessages, setNewAnswerMessages] = useState(new Set());
  const [newMessages, setNewMessages] = useState(new Set());
  const [lastVisitTime, setLastVisitTime] = useState(null);

  // Statistics
  const totalMessages = messages.length;
  const thisMonthMessages = messages.filter(m => {
    const messageDate = new Date(m.createDate);
    const now = new Date();
    return messageDate.getMonth() === now.getMonth() &&
      messageDate.getFullYear() === now.getFullYear();
  }).length;
  const activeMessages = messages.filter(m => m.status === 'פעיל' || !m.status).length;

  // Load user data
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) {
        setCurrentUser({
          userID: userData.userId,
          fullName: userData.name,
          email: userData.email,
          buildingID: userData.buildingId,
          buildingAddress: userData.selectedBuildingInfo?.address || 'לא צוין'
        });
      }
    } catch (error) {
    }
  }, []);

  // Load ALL messages from all buildings
  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVaadsMessages();
      setMessages(data || []);
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Enhanced periodic refresh for real-time updates - THIS IS THE KEY FIX
  useEffect(() => {
    // More frequent polling every 10 seconds for real-time updates
    const interval = setInterval(async () => {
      if (!document.hidden) {
        try {
          const freshMessages = await getVaadsMessages();

          setMessages(prevMessages => {
            // Check if any messages have new answers
            let hasUpdates = false;

            const updatedMessages = freshMessages.map(freshMsg => {
              const existingMsg = prevMessages.find(m => m.id === freshMsg.id);

              if (existingMsg) {
                const oldAnswerCount = existingMsg.answers?.length || 0;
                const newAnswerCount = freshMsg.answers?.length || 0;

                if (newAnswerCount > oldAnswerCount) {
                  hasUpdates = true;

                  // Mark as having new answers if not from current user
                  if (currentUser) {
                    const newAnswers = freshMsg.answers?.slice(oldAnswerCount) || [];
                    const hasNewFromOthers = newAnswers.some(answer => answer.userID !== currentUser.userID);

                    if (hasNewFromOthers) {
                      setNewAnswerMessages(prev => {
                        const newSet = new Set(prev);
                        newSet.add(freshMsg.id);
                        return newSet;
                      });

                      // Auto-remove notification after 15 seconds
                      setTimeout(() => {
                        setNewAnswerMessages(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(freshMsg.id);
                          return newSet;
                        });
                      }, 15000);
                    }
                  }
                }

                return freshMsg;
              } else {
                // New message
                hasUpdates = true;

                if (currentUser && freshMsg.user?.userID !== currentUser.userID) {
                  setNewMessages(prev => {
                    const newSet = new Set(prev).add(freshMsg.id);
                    return newSet;
                  });

                  // Auto-remove new message indicator after 10 seconds
                  setTimeout(() => {
                    setNewMessages(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(freshMsg.id);
                      return newSet;
                    });
                  }, 10000);
                }

                return freshMsg;
              }
            });

            if (hasUpdates) {
              return updatedMessages;
            } else {
              return prevMessages;
            }
          });

        } catch (error) {
        }
      }
    }, 10000); // Every 10 seconds instead of 60

    return () => clearInterval(interval);
  }, [currentUser]);

  // ENHANCED: Listen for notification navigation from Layout
  useEffect(() => {
    const handleNotificationNavigation = async (event) => {
      const { userId, preserveNotificationState, fromNotificationClick } = event.detail;

      if (preserveNotificationState && currentUser?.userID === userId) {

        // Function to apply highlighting once everything is ready
        const applyHighlighting = async () => {
          try {
            // Wait for component to be fully ready and messages to load
            let attempts = 0;
            let currentMessages = messages;

            // Keep checking until we have messages (max 10 attempts = 5 seconds)
            while ((!currentMessages || currentMessages.length === 0) && attempts < 10) {
              await new Promise(resolve => setTimeout(resolve, 500));

              // Try to get fresh messages if local messages are empty
              if (!currentMessages || currentMessages.length === 0) {
                try {
                  currentMessages = await getVaadsMessages();
                } catch (error) {
                }
              } else {
                currentMessages = messages;
              }
              attempts++;
            }

            if (!currentMessages || currentMessages.length === 0) {
              return;
            }


            // Use the stored last visit time to determine what's actually new
            const storedLastVisit = localStorage.getItem('vaadsMessagesLastVisit');
            const lastVisitTime = storedLastVisit ? new Date(storedLastVisit) : new Date(Date.now() - 24 * 60 * 60 * 1000);
            

            const messagesToHighlight = new Set();
            const answersToHighlight = new Set();
            let hasActivity = false;

            currentMessages.forEach(message => {
              // Check for messages created since last visit (from other users)
              const messageDate = new Date(message.createDate);
              if (messageDate > lastVisitTime && message.user?.userID !== currentUser.userID) {
                messagesToHighlight.add(message.id);
                hasActivity = true;
              }

              // Check for answers added since last visit (from other users)
              if (message.answers && message.answers.length > 0) {
                const hasNewAnswers = message.answers.some(answer => {
                  const answerDate = new Date(answer.createdAt || answer.timestamp || answer.updatedDate);
                  const isFromOtherUser = answer.userID !== currentUser.userID;
                  const isAfterLastVisit = answerDate > lastVisitTime;
                  
                  if (isFromOtherUser && isAfterLastVisit) {
                  }
                  
                  return isFromOtherUser && isAfterLastVisit;
                });

                if (hasNewAnswers) {
                  answersToHighlight.add(message.id);
                  hasActivity = true;
                }
              }
            });

            if (hasActivity) {

              // Apply highlighting
              setNewMessages(messagesToHighlight);
              setNewAnswerMessages(answersToHighlight);

              // Auto-clear after 20 seconds
              setTimeout(() => {
                setNewMessages(new Set());
                setNewAnswerMessages(new Set());

                // Update last visit and mark as read
                const now = new Date().toISOString();
                localStorage.setItem('vaadsMessagesLastVisit', now);
                setLastVisitTime(now);

                if (currentUser?.userID) {
                  markMessagesAsRead(currentUser.userID).then(() => {
                    markAsRead();
                    window.dispatchEvent(new CustomEvent('vaadsNotificationProcessed'));
                    window.dispatchEvent(new CustomEvent('vaadsMessagesRead', {
                      detail: { userId: currentUser.userID }
                    }));
                  }).catch(() => {});
                }
              }, 20000);
            } else {
              // Still mark as read
              const now = new Date().toISOString();
              localStorage.setItem('vaadsMessagesLastVisit', now);
              setLastVisitTime(now);

              if (currentUser?.userID) {
                markMessagesAsRead(currentUser.userID).then(() => {
                  markAsRead();
                  window.dispatchEvent(new CustomEvent('vaadsNotificationProcessed'));
                  window.dispatchEvent(new CustomEvent('vaadsMessagesRead', {
                    detail: { userId: currentUser.userID }
                  }));
                }).catch(() => {});
              }
            }

          } catch (error) {
            window.dispatchEvent(new CustomEvent('vaadsNotificationProcessed'));
          }
        };

        // Start the highlighting process
        applyHighlighting();
      }
    };

    window.addEventListener('notificationNavigationToVaads', handleNotificationNavigation);

    return () => {
      window.removeEventListener('notificationNavigationToVaads', handleNotificationNavigation);
    };
  }, [currentUser?.userID, markAsRead, messages, loading]);

  // ALSO add this useEffect to handle the case where the event fires before the component is ready
  useEffect(() => {
    // Check if there's a pending notification navigation when component becomes ready
    const checkPendingNotification = () => {
      const isPending = sessionStorage.getItem('isFromNotificationNavigation');
      if (isPending && !loading && messages.length > 0 && currentUser?.userID) {

        // Dispatch the event to trigger highlighting
        window.dispatchEvent(new CustomEvent('notificationNavigationToVaads', {
          detail: {
            userId: currentUser.userID,
            preserveNotificationState: true,
            fromNotificationClick: true
          }
        }));

        // Clear the flag
        sessionStorage.removeItem('isFromNotificationNavigation');
      }
    };

    // Check when component is ready (not loading and has messages)
    if (!loading && messages.length > 0 && currentUser?.userID) {
      checkPendingNotification();
    }
  }, [loading, messages.length, currentUser?.userID]);

  // Enhanced effect for real-time updates
  useEffect(() => {
    // DON'T update last visit time immediately if coming from notification
    const isFromNotification = sessionStorage.getItem('isFromNotificationNavigation');

    if (!isFromNotification) {
      // Normal visit - update last visit time
      const now = new Date().toISOString();
      setLastVisitTime(now);
      localStorage.setItem('vaadsMessagesLastVisit', now);
    } else {
      // Coming from notification - use stored last visit time
      const storedLastVisit = localStorage.getItem('vaadsMessagesLastVisit');
      if (storedLastVisit) {
        setLastVisitTime(storedLastVisit);
      } else {
        // Fallback: 24 hours ago
        const fallback = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        setLastVisitTime(fallback);
      }
    }

    const handleNewMessage = (event) => {
      const { message } = event.detail;

      setMessages(prev => {
        // Check if message already exists to avoid duplicates
        if (prev.find(m => m.id === message.id)) {
          return prev;
        }
        return [message, ...prev];
      });

      setNewMessages(prev => {
        const newSet = new Set(prev).add(message.id);
        return newSet;
      });

      // Auto-remove "new" status after 10 seconds
      setTimeout(() => {
        setNewMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(message.id);
          return newSet;
        });
      }, 10000);
    };

    const handleMessageAnswered = (event) => {
      const { messageId, answerUserId, answer } = event.detail;


      // IMPROVED: Always refresh from server to get the most up-to-date data
      const refreshMessageFromServer = async () => {
        try {
          const freshMessages = await getVaadsMessages();
          const freshMessage = freshMessages.find(m => m.id === messageId);

          if (freshMessage) {

            setMessages(prev => prev.map(m =>
              m.id === messageId ? freshMessage : m
            ));

            // Update viewing message if it's the same one
            if (viewingAnswersMessage?.id === messageId) {
              setViewingAnswersMessage(freshMessage);
            }
          }
        } catch (error) {
        }
      };

      // Always refresh from server
      refreshMessageFromServer();

      // Handle notifications for other users
      if (currentUser && answerUserId !== currentUser.userID) {
        setNewAnswerMessages(prev => {
          const newSet = new Set(prev).add(messageId);
          return newSet;
        });

        setTimeout(() => {
          setNewAnswerMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(messageId);
            return newSet;
          });
        }, 15000);
      }
    };

    const handleMessageUpdated = (event) => {
      const { messageId, message: updatedMessage } = event.detail;

      // Update the message in the list
      setMessages(prev => prev.map(m =>
        m.id === messageId ? updatedMessage : m
      ));

      // Update viewing message if it's the same one
      if (viewingAnswersMessage?.id === messageId) {
        setViewingAnswersMessage(updatedMessage);
      }
    };

    const handleMessageDeleted = (event) => {
      const { messageId } = event.detail;

      // Remove from all states
      setMessages(prev => prev.filter(m => m.id !== messageId));
      setNewMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
      setNewAnswerMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    };

    // Listen for all message events
    window.addEventListener('vaadsMessageCreated', handleNewMessage);
    window.addEventListener('vaadsMessageAnswered', handleMessageAnswered);
    window.addEventListener('vaadsMessageUpdated', handleMessageUpdated);
    window.addEventListener('vaadsMessageDeleted', handleMessageDeleted);
    window.addEventListener('messageAnswersUpdated', handleMessageUpdated);

    return () => {
      window.removeEventListener('vaadsMessageCreated', handleNewMessage);
      window.removeEventListener('vaadsMessageAnswered', handleMessageAnswered);
      window.removeEventListener('vaadsMessageUpdated', handleMessageUpdated);
      window.removeEventListener('vaadsMessageDeleted', handleMessageDeleted);
      window.removeEventListener('messageAnswersUpdated', handleMessageUpdated);
    };
  }, [currentUser?.userID, viewingAnswersMessage?.id]);

  // Function to get answer count for a message
  const getAnswerCount = useCallback((messageId) => {
    const message = messages.find(m => m.id === messageId);
    const count = message?.answers?.length || 0;
    return count;
  }, [messages]);

  // Function to check if message is new
  const isNewMessage = (messageId) => {
    const isNew = newMessages.has(messageId);
    return isNew;
  };

  // Filter messages
  useEffect(() => {
    let filtered = messages;

    if (searchTerm) {
      filtered = filtered.filter(message => {
        const searchLower = searchTerm.toLowerCase();
        return (
          message.headerMessage?.toLowerCase().includes(searchLower) ||
          message.description?.toLowerCase().includes(searchLower) ||
          message.building?.address?.toLowerCase().includes(searchLower) ||
          message.user?.fullName?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (statusFilter) {
      filtered = filtered.filter(message =>
        (message.status || 'פעיל') === statusFilter
      );
    }

    setFilteredMessages(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, messages]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
  };

  // MODIFIED: More intelligent marking as read - don't immediately mark if came from notification
  useEffect(() => {
    const markMessagesAsReadOnVisit = async () => {
      if (currentUser?.userID) {
        try {
          // Check if this was a notification navigation (if so, delay marking as read)
          const isFromNotification = sessionStorage.getItem('isFromNotificationNavigation');

          if (isFromNotification) {
            sessionStorage.removeItem('isFromNotificationNavigation');
            return;
          }

          // Normal visit - mark as read immediately
          await markMessagesAsRead(currentUser.userID);

          // Mark as read in NotificationService
          markAsRead();

          window.dispatchEvent(new CustomEvent('vaadsMessagesRead', {
            detail: { userId: currentUser.userID }
          }));
        } catch (error) {
        }
      }
    };

    // Only mark as read if not loading and have messages to work with
    if (!loading && currentUser?.userID && messages.length >= 0) {
      markMessagesAsReadOnVisit();
    }
  }, [loading, currentUser?.userID, messages.length, markAsRead]);

  // Pagination
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstMessage, indexOfLastMessage);
  const totalPages = Math.ceil(filteredMessages.length / messagesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Function to mark message as read (remove from new messages/answers sets)
  const markMessageAsRead = useCallback((messageId) => {
    setNewAnswerMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });
    setNewMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(messageId);
      return newSet;
    });

    // Also mark as read in NotificationService
    markAsRead();
  }, [markAsRead]);

  // Event handlers
  const handleAddMessage = () => {
    setEditingMessage(null);
    setShowForm(true);
  };

  const handleEditMessage = (message) => {
    if (currentUser && message.user?.userID === currentUser.userID) {
      setEditingMessage(message);
      setShowForm(true);
    } else {
      alert('אינך יכול לערוך הודעה שלא יצרת');
    }
  };

  const handleDeleteMessage = async (id) => {
    const messageToDelete = messages.find(m => m.id === id);

    if (!currentUser || messageToDelete?.user?.userID !== currentUser.userID) {
      alert('אינך יכול למחוק הודעה שלא יצרת');
      return;
    }

    if (window.confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) {
      try {
        setLoading(true);
        await deleteVaadsMessage(id);
        const updatedMessages = messages.filter(message => message.id !== id);
        setMessages(updatedMessages);

        // Dispatch deletion event
        const eventDetail = {
          messageId: id,
          userId: currentUser.userID
        };
        window.dispatchEvent(new CustomEvent('vaadsMessageDeleted', {
          detail: eventDetail
        }));
      } catch (error) {
        alert('שגיאה במחיקת ההודעה: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Submit answer function
  const handleSubmitAnswer = async (answerText) => {
    try {
      setLoading(true);

      const answerData = {
        answerText: answerText,
        userID: currentUser.userID
      };

      const response = await addAnswerToMessage(viewingAnswersMessage.id, answerData);


      // Update messages with the new answer
      setMessages(prev => prev.map(m => {
        if (m.id === viewingAnswersMessage.id) {
          const updatedMessage = {
            ...m,
            answers: [...(m.answers || []), response.answer],
            updatedDate: new Date().toISOString()
          };

          // Update the viewing message as well
          setViewingAnswersMessage(updatedMessage);

          return updatedMessage;
        }
        return m;
      }));

      // Broadcast event for real-time sync
      const eventDetail = {
        messageId: viewingAnswersMessage.id,
        answerUserId: currentUser.userID,
        answer: response.answer,
        timestamp: new Date().toISOString(),
        messageTitle: viewingAnswersMessage.headerMessage
      };

      window.dispatchEvent(new CustomEvent('vaadsMessageAnswered', {
        detail: eventDetail
      }));

    } catch (error) {
      alert('שגיאה בהוספת התגובה: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAnswers = async (message) => {
    setViewingAnswersMessage(message);
    setShowAnswersViewer(true);

    // Clear the notification dot in Layout by marking messages as read
    if (currentUser?.userID) {
      try {

        // Mark messages as read to clear the Layout red dot
        await markMessagesAsRead(currentUser.userID);

        // Clear notification state
        markAsRead();

        // Clear all local highlighting
        setNewMessages(new Set());
        setNewAnswerMessages(new Set());

        // Update last visit time
        localStorage.setItem('vaadsMessagesLastVisit', new Date().toISOString());

        // Tell Layout that notifications have been processed
        window.dispatchEvent(new CustomEvent('vaadsMessagesRead', {
          detail: { userId: currentUser.userID }
        }));

      } catch (error) {
        // Still clear local state
        setNewMessages(new Set());
        setNewAnswerMessages(new Set());
        markAsRead();
      }
    }
  };

  const handleWhatsAppShare = (message) => {
    const answersText = message.answers && message.answers.length > 0
      ? `\n\n💬 תגובות (${message.answers.length}):\n${message.answers.map(a => `• ${a.userName}: ${a.text}`).join('\n')}`
      : '';

    const whatsappMessage = `📢 הודעה משותפת: ${message.headerMessage}

📝 ${message.description}

🏢 בניין: ${message.building?.address || 'לא צוין'}
👤 נוצר על ידי: ${message.user?.fullName || 'לא צוין'}
📅 תאריך: ${formatDate(message.createDate)}
📊 סטטוס: ${message.status || 'פעיל'}${answersText}

מוזמנים לקרוא ולהגיב!`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  // Message saved handler
  const handleMessageSaved = (savedMessage) => {
    if (editingMessage) {
      // Message updated
      setMessages(prev => prev.map(m =>
        m.id === savedMessage.id ? savedMessage : m
      ));

      const eventDetail = {
        messageId: savedMessage.id,
        message: savedMessage,
        userId: currentUser.userID
      };

      window.dispatchEvent(new CustomEvent('vaadsMessageUpdated', {
        detail: eventDetail
      }));
    } else {
      // New message created
      setMessages(prev => [savedMessage, ...prev]);

      const eventDetail = {
        message: savedMessage,
        userId: currentUser.userID
      };

      window.dispatchEvent(new CustomEvent('vaadsMessageCreated', {
        detail: eventDetail
      }));
    }
    setShowForm(false);
    setEditingMessage(null);
  };

  // Utility functions
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'פעיל':
        return <CheckCircle size={14} />;
      case 'טיפול':
        return <Clock size={14} />;
      case 'סגור':
        return <CheckCircle size={14} />;
      case 'דחוי':
        return <AlertCircle size={14} />;
      default:
        return <CheckCircle size={14} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'פעיל':
        return 'statusActive';
      case 'טיפול':
        return 'statusProcessing';
      case 'סגור':
        return 'statusClosed';
      case 'דחוי':
        return 'statusRejected';
      default:
        return 'statusActive';
    }
  };

  if (showForm) {
    return (
      <VaadsMessageForm
        message={editingMessage}
        onSave={handleMessageSaved}
        onCancel={() => setShowForm(false)}
      />
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
            <h1>הודעות משותפות - כל הבניינים</h1>
          </div>
          <p>ניהול הודעות משותפות לכל הדיירים במערכת</p>
        </div>

        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <MessageSquare size={24} />
            </div>
            <div className={styles.statContent}>
              <h3>סה"כ הודעות</h3>
              <p>{totalMessages}</p>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <Calendar size={24} />
            </div>
            <div className={styles.statContent}>
              <h3>הודעות החודש</h3>
              <p>{thisMonthMessages}</p>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <CheckCircle size={24} />
            </div>
            <div className={styles.statContent}>
              <h3>הודעות פעילות</h3>
              <p>{activeMessages}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <div className={styles.filters}>
          <div className={styles.searchContainer}>
            <Search size={20} className={styles.searchIcon} />
            <input
              type="text"
              placeholder="חיפוש הודעות..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">כל הסטטוסים</option>
            <option value="פעיל">פעיל</option>
            <option value="טיפול">בטיפול</option>
            <option value="סגור">סגור</option>
            <option value="דחוי">דחוי</option>
          </select>

          {(searchTerm || statusFilter) && (
            <button
              className={styles.clearButton}
              onClick={clearFilters}
            >
              נקה מסננים
            </button>
          )}
        </div>

        <button className={styles.addButton} onClick={handleAddMessage}>
          <Plus size={20} />
          הוסף הודעה חדשה
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>טוען הודעות...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className={styles.tableContainer}>
            <table className={styles.tasksTable}>
              <thead>
                <tr>
                  <th>כותרת</th>
                  <th>תיאור</th>
                  <th>בניין</th>
                  <th>נוצר על ידי</th>
                  <th>סטטוס</th>
                  <th>תגובות</th>
                  <th>תאריך יצירה</th>
                  <th>תאריך עדכון</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {currentMessages.length > 0 ? (
                  currentMessages.map((message) => {
                    const hasNewAnswers = newAnswerMessages.has(message.id);
                    const isNew = isNewMessage(message.id);

                    return (
                      <tr key={message.id} className={isNew ? styles.newMessageRow : ''}>
                        <td className={styles.taskDescription}>
                          <div className={styles.titleContent}>
                            <Type size={16} className={styles.titleIcon} />
                            <span>{message.headerMessage}</span>
                            {isNew && (
                              <span className={styles.newBadge}>חדש!</span>
                            )}
                          </div>
                        </td>

                        <td className={styles.taskDescription}>
                          <span title={message.description}>
                            {truncateText(message.description)}
                          </span>
                        </td>

                        <td className={styles.taskUser}>
                          <div className={styles.buildingContent}>
                            <Building size={16} className={styles.buildingIcon} />
                            <span>{message.building?.address || 'לא צוין'}</span>
                          </div>
                        </td>

                        <td className={styles.taskUser}>
                          <div className={styles.userContent}>
                            <User size={16} className={styles.userIcon} />
                            <span>{message.user?.fullName || 'לא צוין'}</span>
                          </div>
                        </td>

                        <td>
                          <span className={`${styles.statusBadge} ${styles[getStatusColor(message.status)]}`}>
                            {getStatusIcon(message.status)}
                            {message.status || 'פעיל'}
                          </span>
                        </td>

                        <td>
                          <div
                            className={`${styles.answersCount} ${hasNewAnswers ? styles.hasNewAnswers : ''
                              } ${isNew ? styles.newMessage : ''
                              }`}
                            onClick={() => handleViewAnswers(message)}
                            title="לחץ לצפייה בתגובות"
                            style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: hasNewAnswers || isNew ? '2px solid #dc3545' : '2px solid #e9ecef',
                              backgroundColor: hasNewAnswers || isNew ? '#fff5f5' : '#f8f9fa',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              minWidth: '80px',
                              justifyContent: 'center'
                            }}
                          >
                            <MessageSquare size={16} color={hasNewAnswers || isNew ? '#dc3545' : '#6c757d'} />
                            <span
                              className={styles.answerCountNumber}
                              style={{
                                fontWeight: hasNewAnswers || isNew ? 'bold' : 'normal',
                                color: hasNewAnswers || isNew ? '#dc3545' : '#495057',
                                fontSize: '14px'
                              }}
                            >
                              {getAnswerCount(message.id)}
                            </span>
                            {(hasNewAnswers || isNew) && (
                              <NotificationDot
                                show={true}
                                size="small"
                                className="answer-notification"
                              />
                            )}
                          </div>
                        </td>

                        <td>{formatDate(message.createDate)}</td>

                        <td>
                          {message.updatedDate ? formatDate(message.updatedDate) : '-'}
                        </td>

                        <td className={styles.actionsCell}>
                          <ActionMenu
                            message={message}
                            onEdit={handleEditMessage}
                            onDelete={handleDeleteMessage}
                            onWhatsAppShare={handleWhatsAppShare}
                            onViewAnswers={handleViewAnswers}
                            loading={loading}
                            currentUser={currentUser}
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className={styles.emptyState}>
                      <div className={styles.emptyContent}>
                        <MessageSquare size={48} className={styles.emptyIcon} />
                        <h3>
                          {searchTerm || statusFilter ?
                            'לא נמצאו הודעות התואמות למסננים שנבחרו' :
                            'עדיין לא נוצרו הודעות משותפות'}
                        </h3>
                        {!searchTerm && !statusFilter && (
                          <p>לחץ על "הוסף הודעה חדשה" כדי ליצור את ההודעה הראשונה</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className={styles.mobileContainer}>
            {currentMessages.length > 0 ? (
              currentMessages.map((message) => (
                <VaadsMessageCard
                  key={message.id}
                  message={message}
                  onEdit={handleEditMessage}
                  onDelete={handleDeleteMessage}
                  onWhatsAppShare={handleWhatsAppShare}
                  onViewAnswers={handleViewAnswers}
                  formatDate={formatDate}
                  loading={loading}
                  getStatusIcon={getStatusIcon}
                  getStatusColor={getStatusColor}
                  currentUser={currentUser}
                  newAnswerMessages={newAnswerMessages}
                  newMessages={newMessages}
                  getAnswerCount={getAnswerCount}
                  isNewMessage={isNewMessage}
                />
              ))
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyContent}>
                  <MessageSquare size={48} className={styles.emptyIcon} />
                  <h3>
                    {searchTerm || statusFilter ?
                      'לא נמצאו הודעות התואמות למסננים' :
                      'אין הודעות'}
                  </h3>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredMessages.length > 0 && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                מציג {indexOfFirstMessage + 1}-{Math.min(indexOfLastMessage, filteredMessages.length)} מתוך {filteredMessages.length} הודעות
              </div>

              <div className={styles.paginationControls}>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                >
                  <ChevronRight size={20} />
                </button>

                <span className={styles.pageInfo}>
                  עמוד {currentPage} מתוך {totalPages}
                </span>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                >
                  <ChevronLeft size={20} />
                </button>
              </div>

              <div className={styles.resultsPerPage}>
                <span>תוצאות בעמוד:</span>
                <select
                  value={messagesPerPage}
                  onChange={(e) => {
                    setMessagesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}

      {/* Answers Viewer Dialog with Integrated Answer Input */}
      {showAnswersViewer && (
        <AnswersViewerDialog
          message={viewingAnswersMessage}
          isOpen={showAnswersViewer}
          onClose={() => {
            setShowAnswersViewer(false);
            setViewingAnswersMessage(null);
          }}
          formatDate={formatDate}
          currentUser={currentUser}
          onSubmitAnswer={handleSubmitAnswer}
          loading={loading}
          onMarkAsRead={markMessageAsRead}
        />
      )}
    </div>
  );
};

export default VaadsMessages;